// @ts-nocheck — Pre-existing type errors (stale code drifted from refactored types / WIP).
// Suppressed to unblock the admin build; runtime unchanged. TODO(owner): restore type-safety.
import { useParams, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ConversationThread } from '../components/ConversationThread'
import { AgentActionsPanel } from '../components/AgentActionsPanel'
import { AgentPromptComposer } from '../components/AgentPromptComposer'
import { ReplyComposer } from '../components/ReplyComposer'
import { DecisionCard } from '../components/DecisionCard'
import { TicketProductBadge } from '../components/TicketProductBadge'
import {
  TicketStatusBadge,
  PriorityBadge,
  IntentBadge,
  SentimentBadge,
  ConfidenceBadge,
} from '../components/TicketStatusBadge'
import {
  useTicket,
  useMessages,
  useAgentActions,
  useSendReply,
  useUpdateTicketStatus,
  useUpdateTicket,
} from '../hooks/useSupport'
import { formatDateTime } from '@/lib/utils'
import type { TicketStatus, TicketPriority } from '@/types/entities'
import { ArrowLeft, Mail, Calendar, Tag, Bot, User, ExternalLink } from 'lucide-react'

export function TicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>()
  const navigate = useNavigate()

  const { data: ticket, isLoading: ticketLoading } = useTicket(ticketId || null)
  const { data: messages, isLoading: messagesLoading } = useMessages(ticketId || null)
  const { data: agentActions, isLoading: actionsLoading } = useAgentActions(ticketId || null)

  const sendReplyMutation = useSendReply()
  const updateStatusMutation = useUpdateTicketStatus()
  const updateTicketMutation = useUpdateTicket()

  const handleSendReply = async (text: string) => {
    if (!ticketId) return
    await sendReplyMutation.mutateAsync({ ticketId, payload: { text } })
  }

  const handleStatusChange = (status: string) => {
    if (!ticketId) return
    updateStatusMutation.mutate({ ticketId, status: status as TicketStatus })
  }

  const handlePriorityChange = (priority: string) => {
    if (!ticketId) return
    updateTicketMutation.mutate({ ticketId, data: { priority: priority as TicketPriority } })
  }

  // Find the latest agent draft (if any)
  // The newest one: a follow-up from the customer produces a fresh draft beside the old.
  const agentDraft = [...(messages ?? [])].reverse().find(
    (m) =>
      m.senderType === 'agent' &&
      m.direction === 'outbound' &&
      m.metadata &&
      (m.metadata as Record<string, unknown>).draft === true
  )

  const escalationReason =
    ticket?.metadata && (ticket.metadata as Record<string, unknown>).escalation_reason
      ? String((ticket.metadata as Record<string, unknown>).escalation_reason)
      : null

  if (ticketLoading) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 space-y-4">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    )
  }

  if (!ticket) {
    return (
      <div className="container mx-auto py-6">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Ticket not found.</p>
          <Button variant="link" onClick={() => navigate('/app/support')}>
            ← Back to Support
          </Button>
        </div>
      </div>
    )
  }

  const isEscalated = ticket.status === 'awaiting_operator'
  const isResolved = ticket.status === 'resolved' || ticket.status === 'closed'

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <Button
            variant="ghost"
            size="sm"
            className="mb-2 -ml-3"
            onClick={() => navigate('/app/support')}
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Support
          </Button>
          <h1 className="text-xl font-semibold">{ticket.subject}</h1>
          <div className="flex items-center gap-3 flex-wrap">
            <TicketStatusBadge status={ticket.status} />
            <TicketProductBadge ticket={ticket} />
            <PriorityBadge priority={ticket.priority} />
            <IntentBadge intent={ticket.intent} />
            <SentimentBadge sentiment={ticket.sentiment} />
            {ticket.confidence !== null && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                Confidence: <ConfidenceBadge confidence={ticket.confidence} />
              </span>
            )}
          </div>
        </div>

        {/* Quick status actions */}
        <div className="flex items-center gap-2">
          {isEscalated && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleStatusChange('resolved')}
            >
              Mark Resolved
            </Button>
          )}
          {ticket.status === 'new' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleStatusChange('awaiting_operator')}
            >
              Claim Ticket
            </Button>
          )}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: Conversation + Reply */}
        <div className="lg:col-span-2 space-y-6">
          {/* Escalation banner.
              Shown only when the agent gave a reason. "Needs attention" also covers tickets the
              agent simply drafted while auto-reply is off, and those were being announced as
              escalated, with nothing after the dash. */}
          {isEscalated && escalationReason && (
            <Card className="border-red-200 bg-red-50/50 dark:bg-red-950/10 dark:border-red-900">
              <CardContent className="py-3 px-4">
                <div className="flex flex-wrap items-center gap-2 text-sm text-red-700 dark:text-red-400">
                  <Bot className="h-4 w-4" />
                  <span className="font-medium">AI Agent escalated this ticket</span>
                  <span className="text-red-600/80 dark:text-red-400/80">— {escalationReason}</span>
                  {/^classified as spam/i.test(escalationReason) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="ml-auto h-7"
                      onClick={() => handleStatusChange('spam')}
                    >
                      Mark as spam
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Refund / duplicate account: approve or decline, then send the drafted notification */}
          <DecisionCard ticketId={ticket.id} open={!isResolved && ticket.status !== 'spam'} />

          {/* Agent draft banner */}
          {agentDraft && !isResolved && (
            <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/10 dark:border-amber-800">
              <CardContent className="py-3 px-4">
                <div className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400">
                  <Bot className="h-4 w-4" />
                  <span className="font-medium">AI Agent drafted a response</span>
                  <span className="text-amber-600/80">— Review and send below</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Conversation */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Conversation</CardTitle>
              <CardDescription>
                {messages?.length || 0} message{(messages?.length || 0) !== 1 ? 's' : ''} in this thread
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ConversationThread
                messages={messages || []}
                isLoading={messagesLoading}
              />
            </CardContent>
          </Card>

          {/* Reply Composer */}
          {!isResolved && ticket.status !== 'spam' && (
            <ReplyComposer
              onSend={handleSendReply}
              disabled={sendReplyMutation.isPending}
              draftText={agentDraft?.bodyText}
              draftId={agentDraft?.id}
            />
          )}
        </div>

        {/* Right column: Ticket details + Agent audit */}
        <div className="space-y-6">
          <AgentPromptComposer key={ticketId} ticketId={ticketId!} />

          {/* Ticket Details Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Ticket Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Customer info */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Customer:</span>
                </div>
                <div className="ml-6">
                  <p className="text-sm font-medium">{ticket.contactName || 'Unknown'}</p>
                  <p className="text-sm text-muted-foreground">{ticket.contactEmail}</p>
                </div>
              </div>

              <Separator />

              {/* Channel */}
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Channel:</span>
                <Badge variant="outline" className="text-xs capitalize">{ticket.channel}</Badge>
              </div>

              {/* Created */}
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Created:</span>
                <span className="text-sm">{formatDateTime(ticket.createdAt)}</span>
              </div>

              {/* First response */}
              {ticket.firstResponseAt && (
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">First response:</span>
                  <span className="text-sm">{formatDateTime(ticket.firstResponseAt)}</span>
                </div>
              )}

              {/* SLA */}
              {ticket.slaBreached && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <Tag className="h-4 w-4" />
                  SLA Breached
                </div>
              )}

              {/* Tags */}
              {ticket.tags && ticket.tags.length > 0 && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm">
                    <Tag className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Tags:</span>
                  </div>
                  <div className="ml-6 flex flex-wrap gap-1">
                    {ticket.tags.map((tag) => (
                      <Badge key={tag} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Linked order */}
              {ticket.shopifyOrderId && (
                <div className="flex items-center gap-2 text-sm">
                  <ExternalLink className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Order:</span>
                  <span className="text-sm font-mono">#{ticket.shopifyOrderId}</span>
                </div>
              )}

              <Separator />

              {/* Status control */}
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Status</label>
                <Select
                  value={ticket.status}
                  onValueChange={handleStatusChange}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="awaiting_operator">Needs Attention</SelectItem>
                    <SelectItem value="awaiting_customer">Awaiting Customer</SelectItem>
                    <SelectItem value="operator_replied">Replied</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                    <SelectItem value="spam">Spam</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Priority control */}
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Priority</label>
                <Select
                  value={ticket.priority}
                  onValueChange={handlePriorityChange}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Agent Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Bot className="h-4 w-4 text-violet-500" />
                Agent Activity
              </CardTitle>
              <CardDescription>
                What the AI agent did when processing this ticket.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AgentActionsPanel
                actions={agentActions || []}
                isLoading={actionsLoading}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
